import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'

import equal from 'fast-deep-equal'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { BuilderSteps } from '@enums'
import { usePrevious } from '@hooks/usePrevious'
import { IApplicationState } from '@store'
import {
  saveCampaign,
  SaveOutcome,
  selectCampaignIsSaving,
  selectCampaignLastSaveOutcome,
} from '@store/campaigns'
import { IDraft, saveDraft, selectDraft } from '@store/drafts'
import { selectMinimumInvoiceBudgetForUserRegion } from '@store/me'
import { selectIsAnyUploading } from '@store/moodboard'
import { Form, IWithFormValidationBaseProps, withFormValidation } from '@tribegroup'
import { IValidationResult } from '@tribegroup'
import { validateSteps } from './rules'

export interface IBuilderValidationContextProps
  extends IBuilderValidationProviderProps,
    IWithFormValidationBaseProps {
  updateForm: (draftProps: Partial<IDraft>) => void
  validationResults: Record<string, IValidationResult>
  clearFieldValidation: (
    eventorName: React.FormEvent<HTMLInputElement | HTMLTextAreaElement> | string | string[],
  ) => void
  validateFieldByNameAndValue: (name: string, value: string | number) => void
  stepsValidation: Record<BuilderSteps, boolean>
  hasStepValidationErrors: boolean
  setContinueCallback: (callback) => void
  setEnableActions: (enable: boolean) => void
  checkValidationResults: (fieldName: string) => boolean
  getValidationMessage: (fieldName: string) => string | undefined
  isEnableActions: boolean
  formChanged?: boolean
  setFormChanged: (enable: boolean) => void
  setEnableSubmit: (enable: boolean) => void
  isEnableSubmit: boolean
}

interface IBuilderValidationProviderProps {
  currentStep: BuilderSteps
  nextStep?: BuilderSteps
}

interface IInternalProps
  extends IBuilderValidationProviderProps,
    IBuilderValidationContextProps,
    WrappedComponentProps {
  isUpdating?: boolean
  setForm: (form: React.Ref<HTMLFormElement>) => void
  draft: IDraft
  saveCampaign: typeof saveCampaign
  saveDraft: typeof saveDraft
  minimumInvoiceBudget?: number
  isAnyUploading?: boolean
  history: any
  isSaving?: boolean
  lastSaveOutcome?: SaveOutcome
}

const builderSteps = Object.values(BuilderSteps)

export const BuilderValidationContext = React.createContext({
  currentStep: {},
} as IBuilderValidationContextProps)

export const BuilderValidationProvider: React.FC<PropsWithChildren<IInternalProps>> = ({
  children,
  currentStep,
  nextStep,
  validate,
  clearFieldValidation,
  validateFieldByNameAndValue,
  validationResults,
  draft,
  saveCampaign: saveCampaignAction,
  saveDraft: saveDraftAction,
  setForm,
  isAnyUploading,
  history,
  isSaving,
  lastSaveOutcome,
}) => {
  const completedStep = draft.step_completed
  const completedStepIndex = builderSteps.findIndex((step) => step === completedStep)
  const currentStepIndex = builderSteps.findIndex((step) => step === currentStep)

  const [enableSubmit, setEnableSubmit] = useState(currentStep !== BuilderSteps.BRAND)
  const [enableActions, setEnableActions] = useState(true)
  const [continueCallback, setContinueCallback] = useState<any>(() => () => false)
  const [formLoaded, setFormLoaded] = useState(false)
  const [formChanged, setFormChanged] = useState(false)

  const onContinue = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      if (continueCallback) {
        continueCallback({
          nextStep,
          draft,
          currentStep,
          history,
          saveDraft: saveDraftAction,
          saveCampaign: saveCampaignAction,
          isAnyUploading,
        })
      }
    },
    [
      continueCallback,
      nextStep,
      draft,
      currentStep,
      history,
      saveDraftAction,
      saveCampaignAction,
      isAnyUploading,
    ],
  )

  const onLoad = useCallback(() => {
    if (!formLoaded && currentStepIndex <= completedStepIndex) {
      validate()
    }
    setFormLoaded(true)
  }, [formLoaded, currentStepIndex, completedStepIndex, validate])

  const setContinueCallbackWrapper = useCallback((callback) => {
    setContinueCallback(() => callback)
  }, [])

  const setEnableSubmitWrapper = useCallback((enable: boolean) => {
    setEnableSubmit(enable)
  }, [])

  const setEnableActionsWrapper = useCallback((enable: boolean) => {
    setEnableActions(enable)
  }, [])

  const setFormChangedWrapper = useCallback((changed: boolean) => {
    setFormChanged(changed)
  }, [])

  const checkValidationResults = useCallback(
    (fieldName: string) => {
      return (
        Boolean(validationResults && validationResults[fieldName]) &&
        validationResults[fieldName].hasError
      )
    },
    [validationResults],
  )

  const getValidationMessage = useCallback(
    (fieldName: string) => {
      return (
        validationResults &&
        validationResults[fieldName] &&
        validationResults[fieldName].errorMessage
      )
    },
    [validationResults],
  )

  const updateForm = useCallback(
    (draftProps: Partial<IDraft>) => {
      const updatedProperties = getFormChanges(draftProps, draft)
      if (updatedProperties.length > 0) {
        saveDraftAction({ ...draft, ...draftProps })
        setFormChanged(true)
      }
    },
    [draft, saveDraftAction],
  )

  // Reset formChanged only when a save WE triggered actually succeeded.
  //
  // This used to be inferred from `campaign` (the persisted record in
  // campaignsById) changing reference on the falling edge of isSaving. That
  // broke two ways (BRAND-9475 review):
  //  1. campaigns/reducers.ts wraps campaignsById in `returnSecondIfDeepEqual`,
  //     so a no-op save — the server echoing back a campaign that's deep-equal
  //     to what's already stored — deliberately leaves the reference
  //     unchanged. Reference identity then misreads a genuine success as a
  //     failure and Save never re-disables.
  //  2. DISABLE_SUCCESS and a plain campaign FETCH_SUCCESS share the same
  //     reducer branch as SAVE_SUCCESS (isSaving:false + campaignsById
  //     replacement). Either one landing while a real save is still in
  //     flight can "forge" the falling edge and falsely clear formChanged
  //     before the actual save has resolved.
  // `lastSaveOutcome` is an explicit signal set only by the campaigns
  // reducer's SAVE_SUCCESS/SAVE_ERROR branches, so neither failure mode
  // applies: gate the reset on isSaving's falling edge AND a 'success'
  // outcome. A failed save must still leave the form dirty so the user can
  // retry; resetting on every isSaving-false transition regardless of
  // outcome was reviewed and rejected for exactly that reason (superseded PR
  // #3461).
  //
  // Note (PR #3465 review): `isSaving`/`lastSaveOutcome` are intentionally
  // global campaign-level flags (one in-flight save per campaign, sourced
  // from campaigns/reducers.ts), not scoped to this provider instance or the
  // save this component happened to trigger. That's what lets the
  // FETCH_SUCCESS/DISABLE_SUCCESS "forged falling edge" cases above happen in
  // the first place — the gate below only closes the specific hole where the
  // *outcome* is misread, not the fact that saves are tracked globally.
  const wasSaving = usePrevious(isSaving)

  useEffect(() => {
    if (!isSaving && wasSaving && lastSaveOutcome === 'success') {
      setFormChanged(false)
    }
  }, [isSaving, wasSaving, lastSaveOutcome])

  const stepsValidation = useMemo(() => validateSteps(draft), [draft])

  const contextProps: IBuilderValidationContextProps = useMemo(
    () => ({
      currentStep,
      nextStep,
      stepsValidation,
      validate,
      validateFieldByNameAndValue,
      checkValidationResults,
      getValidationMessage,
      clearFieldValidation,
      hasStepValidationErrors: !Object.values(stepsValidation).every(Boolean),
      setContinueCallback: setContinueCallbackWrapper,
      setEnableSubmit: setEnableSubmitWrapper,
      isEnableSubmit: enableSubmit,
      setEnableActions: setEnableActionsWrapper,
      isEnableActions: enableActions,
      updateForm,
      formChanged,

      // should eventually make these internal only when no longer used by consuming components
      validationResults,

      // TODO: Deprecate
      setFormChanged: setFormChangedWrapper,
      validationRules: {} as any,
    }),
    [
      currentStep,
      nextStep,
      stepsValidation,
      validate,
      validateFieldByNameAndValue,
      checkValidationResults,
      getValidationMessage,
      clearFieldValidation,
      setContinueCallbackWrapper,
      setEnableSubmitWrapper,
      enableSubmit,
      setEnableActionsWrapper,
      enableActions,
      updateForm,
      formChanged,
      validationResults,
      setFormChangedWrapper,
    ],
  )

  return (
    <BuilderValidationContext.Provider value={contextProps}>
      <Form onBeforeSubmit={validate} onSubmit={onContinue} setForm={setForm} onLoad={onLoad}>
        {children}
      </Form>
    </BuilderValidationContext.Provider>
  )
}

export const getFormChanges = (draftProps: IDraft, lastDraft: IDraft) => {
  const updatedProperties = Object.keys(draftProps).filter((key) => {
    if (
      key === 'submission_type' &&
      lastDraft.supported_submission_types &&
      lastDraft.supported_submission_types[0]
    ) {
      return false
    }
    const alreadyEmpty = draftProps[key] === '' && lastDraft[key] === undefined
    const replacedWithAnotherEmptyArray =
      draftProps[key] &&
      draftProps[key].length === 0 &&
      lastDraft[key] &&
      lastDraft[key].length === 0
    const uninitializedAndReplacedWithEmpty =
      draftProps[key] && draftProps[key].length === 0 && lastDraft[key] === undefined
    if (alreadyEmpty || replacedWithAnotherEmptyArray || uninitializedAndReplacedWithEmpty) {
      return false
    }
    return !equal(draftProps[key], lastDraft[key])
  })
  return updatedProperties
}

// Apply HOCs to the base provider
const BuilderValidationProviderWithHOCs = injectIntl(
  withFormValidation(BuilderValidationProvider as any) as any,
) as any

// Connected component using hooks
const ConnectedBuilderValidationProvider: React.FC<
  PropsWithChildren<IBuilderValidationProviderProps>
> = ({ children, currentStep }) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const currentStepIndex = Object.values(BuilderSteps).findIndex((step) => step === currentStep)
  const nextStep = Object.values(BuilderSteps)[currentStepIndex + 1]
  const draft = useSelector(selectDraft)
  const minimumInvoiceBudget = useSelector(selectMinimumInvoiceBudgetForUserRegion)
  const isAnyUploadingState = useSelector((state: IApplicationState) =>
    draft.id ? selectIsAnyUploading(state, draft.id) : false,
  )
  const isAnyUploading = draft.id ? isAnyUploadingState : undefined
  const isSaving = useSelector(selectCampaignIsSaving)
  const lastSaveOutcome = useSelector(selectCampaignLastSaveOutcome)

  return (
    <BuilderValidationProviderWithHOCs
      currentStep={currentStep}
      nextStep={nextStep}
      draft={draft}
      saveCampaign={(...args) => dispatch(saveCampaign(...args))}
      saveDraft={(d) => dispatch(saveDraft(d))}
      minimumInvoiceBudget={minimumInvoiceBudget}
      isAnyUploading={isAnyUploading}
      isSaving={isSaving}
      lastSaveOutcome={lastSaveOutcome}
      history={history}
    >
      {children}
    </BuilderValidationProviderWithHOCs>
  )
}

export default ConnectedBuilderValidationProvider
